import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, User, Package, LogOut, LogIn } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CATEGORIES } from '../data/mock';

const Header = () => {
  const { itemCount, total } = useCart();
  const { user, login, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(search)}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
      {/* Top bar */}
      <div className="bg-emerald-700 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex justify-between items-center">
          <span>Ecom Seekho — Sabse Sasta, Sabse Achha</span>
          <span className="hidden md:inline">Wholesale prices | Bulk orders welcome</span>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-2 md:py-3 flex items-center gap-2 md:gap-4">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 flex items-center gap-2">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_seekho-admin/artifacts/2biwyt6k_6305294127389675025.jpg"
            alt="Ecom Seekho"
            className="w-9 h-9 md:w-12 md:h-12 rounded-md object-cover border border-yellow-200"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-bold text-base md:text-lg text-gray-900">Ecom Seekho</span>
            <span className="text-[10px] text-gray-500 -mt-1">Wholesale Marketplace</span>
          </div>
        </Link>

        {/* Search */}
        <form onSubmit={submitSearch} className="flex-1 hidden md:flex">
          <div className="flex w-full max-w-2xl mx-auto">
            <Input
              placeholder="Search for products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-r-none focus-visible:ring-emerald-500"
            />
            <Button
              type="submit"
              className="rounded-l-none bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </form>

        {/* Right actions */}
        <div className="flex items-center gap-2 ml-auto">
          {user ? (
            <div className="relative hidden md:block">
              <button
                onClick={() => setUserMenu(!userMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">
                    {user.name?.charAt(0) || 'U'}
                  </div>
                )}
                <span className="text-sm text-gray-700 max-w-[120px] truncate">{user.name?.split(' ')[0]}</span>
              </button>
              {userMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <div className="p-3 border-b">
                    <div className="text-sm font-medium text-gray-800 truncate">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <button
                    onClick={async () => { await logout(); setUserMenu(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={login}
              className="hidden md:flex items-center gap-1 text-sm text-gray-700 hover:text-emerald-700 px-3 py-2"
            >
              <LogIn className="w-4 h-4" />
              <span>Login</span>
            </button>
          )}
          <Link
            to="/admin"
            className="hidden md:flex items-center gap-1 text-sm text-gray-700 hover:text-emerald-700 px-3 py-2"
          >
            <User className="w-4 h-4" />
            <span>Admin</span>
          </Link>
          <Link
            to="/cart"
            className="flex items-center gap-1.5 md:gap-2 bg-emerald-50 hover:bg-emerald-100 px-2 md:px-3 py-1.5 md:py-2 rounded-md text-emerald-800 border border-emerald-200 transition-colors"
          >
            <div className="relative">
              <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {itemCount}
                </span>
              )}
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-[10px] text-gray-600">{itemCount} items</span>
              <span className="text-sm font-semibold">₹{total.toFixed(2)}</span>
            </div>
          </Link>
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Category nav */}
      <nav className="bg-white border-t border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-1 overflow-x-auto text-sm py-1 whitespace-nowrap">
            <li>
              <Link
                to="/"
                className="px-3 py-2 inline-block font-semibold text-emerald-700 hover:bg-emerald-50 rounded"
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/shop"
                className="px-3 py-2 inline-block font-semibold text-orange-600 hover:bg-orange-50 rounded"
              >
                All Products
              </Link>
            </li>
            {CATEGORIES.slice(0, 10).map((c) => (
              <li key={c.id}>
                <Link
                  to={`/shop?category=${c.id}`}
                  className="px-3 py-2 inline-block text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded"
                >
                  {c.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/cart"
                className="px-3 py-2 inline-block text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded flex items-center gap-1"
              >
                <Package className="w-3.5 h-3.5" /> Cart
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <form onSubmit={submitSearch} className="p-3 flex gap-2">
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Search className="w-4 h-4" />
            </Button>
          </form>
          <ul className="p-2 flex flex-col text-sm">
            <li>
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded hover:bg-emerald-50"
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/shop"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded hover:bg-emerald-50"
              >
                Shop
              </Link>
            </li>
            {CATEGORIES.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/shop?category=${c.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 rounded hover:bg-emerald-50"
                >
                  {c.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 rounded hover:bg-emerald-50 border-t"
              >
                Admin Panel
              </Link>
            </li>
            {user ? (
              <li>
                <button
                  onClick={async () => { await logout(); setMobileOpen(false); }}
                  className="w-full text-left px-3 py-2 rounded hover:bg-red-50 text-red-600"
                >
                  Sign out ({user.name?.split(' ')[0]})
                </button>
              </li>
            ) : (
              <li>
                <button
                  onClick={() => { setMobileOpen(false); login(); }}
                  className="w-full text-left px-3 py-2 rounded hover:bg-emerald-50 text-emerald-700 font-medium"
                >
                  Login with Google
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
